import { WorkflowStep, ProductWorkflow } from '@/types/fragments';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Outcome {
  id: string;
}

interface Dialog {
  show: boolean;
  id?: string;
}

interface ThumbDiameterDialog extends Dialog {
  value?: number;
}

interface WorkflowState {
  currentStep: WorkflowStep | undefined;
  productionLocation: ProductWorkflow | undefined;
  material: ProductWorkflow | undefined;
  thumbSupport: ProductWorkflow | undefined;
  braceLength: ProductWorkflow | undefined;
  outCome: Outcome;
  thumbDiameter: ThumbDiameterDialog | undefined;
  openingLocation: ProductWorkflow | undefined;
  hinges: ProductWorkflow | undefined;
  padding: ProductWorkflow | undefined;
  straps: ProductWorkflow | undefined;
  paddingThickness: ThumbDiameterDialog | undefined;
  // Not persisted. Code acting on an absent selection (e.g. the workflow step guard) must
  // wait for this, or it reads the pre-hydration defaults as "nothing selected".
  hasHydrated: boolean;
  creatingWorkflowId: string | null;

  setCurrentStep: (step: WorkflowStep) => void;
  setProductionLocation: (location: ProductWorkflow) => void;
  setMaterial: (material: ProductWorkflow) => void;
  setThumbSupport: (thumbSupport: ProductWorkflow) => void;
  setBraceLength: (braceLength: ProductWorkflow) => void;
  setOutCome: (outCome: Outcome) => void;
  setThumbDiameter: (step: boolean, id?: string, value?: number) => void;
  resetThumbDiameter: () => void;
  setOpeningLocation: (openingLocation: ProductWorkflow) => void;
  setHasHydrated: (value: boolean) => void;
  setHinges: (hinges: ProductWorkflow) => void;
  setPadding: (padding: ProductWorkflow) => void;
  setStraps: (straps: ProductWorkflow) => void;
  setPaddingThickness: (step: boolean, id?: string, value?: number) => void;
  resetPaddingThickness: () => void;
  reset: () => void;
  resetOutCome: () => void;
  setCreatingWorkflowId: (value: string | null) => void;
}

export const workflowStore = create<WorkflowState>()(
  persist(
    (set) => ({
      currentStep: undefined,
      productionLocation: undefined,
      material: undefined,
      thumbSupport: undefined,
      braceLength: undefined,
      outCome: undefined,
      thumbDiameter: undefined,
      openingLocation: undefined,
      hasHydrated: false,
      hinges: undefined,
      padding: undefined,
      paddingThickness: undefined,
      straps: undefined,
      creatingWorkflowId: null,

      setCurrentStep: (step) => set({ currentStep: step }),
      setProductionLocation: (location) => set({ productionLocation: location }),
      setMaterial: (material) => set({ material }),
      setThumbSupport: (thumbSupport) => set({ thumbSupport }),
      setBraceLength: (braceLength) => set({ braceLength }),
      setOutCome: (outCome) => set({ outCome }),
      setThumbDiameter: (show, id, value) => set({ thumbDiameter: { show, id, value } }),
      resetThumbDiameter: () => set({ thumbDiameter: undefined }),
      setOpeningLocation: (openingLocation) => set({ openingLocation }),
      setHinges: (hinges) => set({ hinges }),
      setPadding: (padding) => set({ padding }),
      setStraps: (straps) => set({ straps }),
      setPaddingThickness: (show, id, value) => set({ paddingThickness: { show, id, value } }),
      resetPaddingThickness: () => set({ paddingThickness: undefined }),
      setHasHydrated: (value) => set({ hasHydrated: value }),
      setCreatingWorkflowId: (value) => set({ creatingWorkflowId: value }),

      reset: () =>
        set({
          currentStep: undefined,
          productionLocation: undefined,
          material: undefined,
          thumbSupport: undefined,
          braceLength: undefined,
          outCome: undefined,
          thumbDiameter: undefined,
          openingLocation: undefined,
          hinges: undefined,
          padding: undefined,
          straps: undefined,
          paddingThickness: undefined,
        }),
      resetOutCome: () =>
        set({
          outCome: undefined,
        }),
    }),
    {
      name: 'workflow-storage',
      partialize: (state) => ({
        currentStep: state.currentStep,
        productionLocation: state.productionLocation,
        material: state.material,
        thumbSupport: state.thumbSupport,
        braceLength: state.braceLength,
        outCome: state.outCome,
        thumbDiameter: state.thumbDiameter,
        openingLocation: state.openingLocation,
        hinges: state.hinges,
        padding: state.padding,
        straps: state.straps,
        paddingThickness: state.paddingThickness,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);
